import React, { useEffect, useMemo, useState } from 'react';
import { fmtDateTime, todayIso, syncDateRange } from '../utils/dateUtils';

function BarReports({ token, selectedPropertyId }) {
  const colors = {
    primary: '#2c3e50',
    accent: '#3498db',
    success: '#27ae60',
    text: '#2c3e50',
    textLight: '#7f8c8d',
    background: '#ecf0f1',
    cardBg: '#ffffff',
    border: '#bdc3c7',
    headerBg: '#34495e',
    headerText: '#ffffff'
  };

  const detectMobileUi = () => {
    if (typeof window === 'undefined') return false;
    const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(window.navigator?.userAgent || '');
    return (coarsePointer || uaMobile) && window.innerWidth <= 1024;
  };
  const [isMobile, setIsMobile] = useState(() => detectMobileUi());
  const today = todayIso();
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [forecastItems, setForecastItems] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    from: today,
    to: today,
    payment_type: '',
    item_code: ''
  });

  const resolvePropertyId = () => String(selectedPropertyId || localStorage.getItem('selectedPropertyId') || '').trim();
  const authHeaders = () => ({ Authorization: `Bearer ${token}`, 'x-property-id': resolvePropertyId() });
  const tapMin = { minHeight: '44px' };

  useEffect(() => {
    const onResize = () => setIsMobile(detectMobileUi());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const inDateRange = (dateValue) => {
    if (!dateValue) return false;
    const dt = new Date(dateValue);
    if (Number.isNaN(dt.getTime())) return false;
    if (filters.from) {
      const fromDate = new Date(`${filters.from}T00:00:00`);
      if (dt < fromDate) return false;
    }
    if (filters.to) {
      const toDate = new Date(`${filters.to}T23:59:59`);
      if (dt > toDate) return false;
    }
    return true;
  };

  const loadAll = async () => {
    if (!resolvePropertyId()) {
      setError('Select a property first from Hotel Config');
      setOrders([]);
      setInventory([]);
      setAdjustments([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [ordersRes, inventoryRes, adjustmentsRes, forecastRes] = await Promise.all([
        fetch('/api/orders', { headers: authHeaders() }),
        fetch('/api/inventory', { headers: authHeaders() }),
        fetch('/api/inventory/adjustments', { headers: authHeaders() }),
        fetch('/api/bar/reports/items/demand-forecast?limit=25', { headers: authHeaders() })
      ]);
      const [ordersData, inventoryData, adjustmentsData, forecastData] = await Promise.all([
        ordersRes.json(),
        inventoryRes.json(),
        adjustmentsRes.json(),
        forecastRes.json()
      ]);

      if (!ordersRes.ok) throw new Error(ordersData?.error || 'Failed to load orders');
      if (!inventoryRes.ok) throw new Error(inventoryData?.error || 'Failed to load inventory');
      if (!adjustmentsRes.ok) throw new Error(adjustmentsData?.error || 'Failed to load stock adjustments');
      if (!forecastRes.ok) throw new Error(forecastData?.error || 'Failed to load demand forecast');

      setOrders(Array.isArray(ordersData) ? ordersData : []);
      setInventory(Array.isArray(inventoryData) ? inventoryData : []);
      setAdjustments(Array.isArray(adjustmentsData) ? adjustmentsData : []);
      setForecastItems(Array.isArray(forecastData?.items) ? forecastData.items : []);
    } catch (err) {
      setError(err?.message || 'Failed to load reports');
      setOrders([]);
      setInventory([]);
      setAdjustments([]);
      setForecastItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [token, selectedPropertyId]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (!inDateRange(o.order_date)) return false;
      if (filters.payment_type && String(o.payment_type || '') !== filters.payment_type) return false;
      return true;
    });
  }, [orders, filters]);

  const filteredAdjustments = useMemo(() => {
    return adjustments.filter((a) => {
      if (!inDateRange(a.created_at)) return false;
      if (filters.item_code && String(a.item_code || '') !== filters.item_code) return false;
      return true;
    });
  }, [adjustments, filters]);

  const salesSummary = useMemo(() => {
    const bills = filteredOrders.length;
    const subtotal = filteredOrders.reduce((sum, o) => sum + Number(o.subtotal || 0), 0);
    const tax = filteredOrders.reduce((sum, o) => sum + Number(o.gst_amount || 0), 0);
    const total = filteredOrders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);
    const avgBill = bills > 0 ? (total / bills) : 0;
    return { bills, subtotal, tax, total, avgBill };
  }, [filteredOrders]);

  const paymentMixRows = useMemo(() => {
    const map = {};
    filteredOrders.forEach((o) => {
      const key = String(o.payment_type || 'UNKNOWN');
      if (!map[key]) map[key] = { payment_type: key, bills: 0, amount: 0 };
      map[key].bills += 1;
      map[key].amount += Number(o.total_amount || 0);
    });
    return Object.values(map).sort((a, b) => Number(b.amount) - Number(a.amount));
  }, [filteredOrders]);

  const lowStockRows = useMemo(() => {
    return inventory
      .map((i) => ({
        ...i,
        shortage: Math.max(0, Number(i.reorder_level || 0) - Number(i.available_quantity_ml || 0))
      }))
      .filter((i) => Number(i.shortage) > 0)
      .sort((a, b) => Number(b.shortage) - Number(a.shortage));
  }, [inventory]);

  const fmt = (n) => Number(n || 0).toFixed(2);
  const escapeCsv = (value) => {
    const str = String(value ?? '');
    if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
    return str;
  };
  const downloadFile = (content, mimeType, fileName) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const exportCsv = (fileName, columns, rows) => {
    const header = columns.map((c) => escapeCsv(c.label)).join(',');
    const body = rows.map((r) => columns.map((c) => escapeCsv(c.value(r))).join(',')).join('\n');
    downloadFile(`${header}\n${body}`, 'text/csv;charset=utf-8;', `${fileName}.csv`);
  };
  const escapeHtml = (value) => String(value ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const exportExcel = (fileName, columns, rows) => {
    const head = `<tr>${columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join('')}</tr>`;
    const body = rows.map((r) => `<tr>${columns.map((c) => `<td>${escapeHtml(c.value(r))}</td>`).join('')}</tr>`).join('');
    const html = `<html><head><meta charset="utf-8" /></head><body><table border="1">${head}${body}</table></body></html>`;
    downloadFile(html, 'application/vnd.ms-excel;charset=utf-8;', `${fileName}.xls`);
  };
  const exportButtons = (fileName, columns, rows) => (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <button onClick={() => exportCsv(fileName, columns, rows)} style={{ ...tapMin, padding: '0.5rem 1rem', borderRadius: '6px', border: `1px solid ${colors.border}`, background: colors.cardBg, color: colors.primary, cursor: 'pointer', fontWeight: 'bold' }}>CSV</button>
      <button onClick={() => exportExcel(fileName, columns, rows)} className="btn-success" style={{ ...tapMin, padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 'bold' }}>Excel</button>
    </div>
  );

  const cardStyle = { background: colors.cardBg, borderRadius: '12px', padding: '1.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: '150px' };
  const tableContainerStyle = { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${colors.border}`, background: colors.cardBg };
  const tableStyle = { width: '100%', minWidth: '800px', borderCollapse: 'collapse', fontSize: '0.9rem' };
  const thStyle = { padding: '1rem', textAlign: 'left', background: colors.headerBg, color: colors.headerText, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `2px solid ${colors.primary}`, position: 'sticky', top: 0, zIndex: 1 };
  const tdStyle = { padding: '0.85rem 1rem', borderBottom: `1px solid ${colors.border}`, color: colors.text };
  const trHoverStyle = { transition: 'background-color 0.2s' }; // Note: React inline styles don't do hover easily, but setting structure helps.
  const headerStyle = { color: colors.primary, marginBottom: '0.5rem', fontWeight: '700', fontSize: '1.25rem' }; // Fixing white color issue by explicitly setting dark color

  return (
    <div style={{ padding: isMobile ? '1rem' : '1.5rem', backgroundColor: colors.background, minHeight: '100vh' }}>
      <h2 style={{ color: colors.primary, marginBottom: '1.5rem', fontWeight: '800', borderBottom: `3px solid ${colors.accent}`, paddingBottom: '0.5rem', display: 'inline-block' }}>BAR Reports</h2>
      {error && <div style={{ color: colors.headerText, background: colors.primary, padding: '1rem', borderRadius: '8px', marginBottom: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem', background: colors.cardBg, padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
        <input type="date" value={filters.from} onChange={(e) => setFilters(syncDateRange({ ...filters, from: e.target.value }, 'from', 'to', 'from'))} style={{ ...tapMin, padding: '0.75rem', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }} />
        <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilters(syncDateRange({ ...filters, to: e.target.value }, 'from', 'to', 'to'))} style={{ ...tapMin, padding: '0.75rem', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }} />
        <select value={filters.payment_type} onChange={(e) => setFilters({ ...filters, payment_type: e.target.value })} style={{ ...tapMin, padding: '0.75rem', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '14px', outline: 'none' }}>
          <option value="">All Payment Types</option>
          {[...new Set(orders.map((o) => String(o.payment_type || '').trim()).filter(Boolean))].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.item_code} onChange={(e) => setFilters({ ...filters, item_code: e.target.value })} style={{ ...tapMin, padding: '0.75rem', borderRadius: '8px', border: `1px solid ${colors.border}`, fontSize: '14px', outline: 'none' }}>
          <option value="">All Adjustment Items</option>
          {[...new Set(adjustments.map((a) => String(a.item_code || '').trim()).filter(Boolean))].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={loadAll} disabled={loading} style={{ ...tapMin, padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', background: colors.primary, color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>{loading ? 'Loading...' : 'Refresh'}</button>
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', background: colors.background, padding: '1rem', borderRadius: '8px' }}>
          <h3 style={headerStyle}>Order List</h3>
          {exportButtons(
            'bar_order_list',
            [
              { label: 'Order No', value: (r) => r.order_no },
              { label: 'Date', value: (r) => r.order_date ? fmtDateTime(r.order_date) : '-' },
              { label: 'Guest', value: (r) => r.guest_name || '-' },
              { label: 'Table', value: (r) => r.table_no || '-' },
              { label: 'Payment Type', value: (r) => r.payment_type || '-' },
              { label: 'Subtotal', value: (r) => fmt(r.subtotal) },
              { label: 'Tax', value: (r) => fmt(r.gst_amount) },
              { label: 'Total', value: (r) => fmt(r.total_amount) },
              { label: 'Status', value: (r) => r.status || '-' }
            ],
            filteredOrders
          )}
        </div>
        <div style={{ ...tableContainerStyle, maxHeight: '500px', overflowY: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Order No</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Guest</th>
                <th style={thStyle}>Table</th>
                <th style={thStyle}>Payment Type</th>
                <th style={thStyle}>Subtotal</th>
                <th style={thStyle}>Tax</th>
                <th style={thStyle}>Total</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr><td style={tdStyle} colSpan={9}>No orders found</td></tr>
              ) : filteredOrders.map((o) => (
                <tr key={o.id || o.order_no}>
                  <td style={tdStyle}>{o.order_no}</td>
                  <td style={tdStyle}>{o.order_date ? fmtDateTime(o.order_date) : '-'}</td>
                  <td style={tdStyle}>{o.guest_name || '-'}</td>
                  <td style={tdStyle}>{o.table_no || '-'}</td>
                  <td style={tdStyle}>{o.payment_type || '-'}</td>
                  <td style={tdStyle}>{fmt(o.subtotal)}</td>
                  <td style={tdStyle}>{fmt(o.gst_amount)}</td>
                  <td style={tdStyle}>{fmt(o.total_amount)}</td>
                  <td style={tdStyle}>{o.status || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.5rem', flexWrap: 'wrap', background: colors.background, padding: '1rem', borderRadius: '8px' }}>
        <h3 style={headerStyle}>Sales Summary</h3>
        {exportButtons(
          'bar_sales_summary',
          [
            { label: 'Bills', value: (r) => r.bills },
            { label: 'Gross Sales', value: (r) => fmt(r.subtotal) },
            { label: 'Tax', value: (r) => fmt(r.tax) },
            { label: 'Net Sales', value: (r) => fmt(r.total) },
            { label: 'Avg Bill', value: (r) => fmt(r.avgBill) }
          ],
          [salesSummary]
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={cardStyle}><div>Bills</div><strong>{salesSummary.bills}</strong></div>
        <div style={cardStyle}><div>Gross Sales</div><strong>{fmt(salesSummary.subtotal)}</strong></div>
        <div style={cardStyle}><div>Tax</div><strong>{fmt(salesSummary.tax)}</strong></div>
        <div style={cardStyle}><div>Net Sales</div><strong>{fmt(salesSummary.total)}</strong></div>
        <div style={cardStyle}><div>Avg Bill</div><strong>{fmt(salesSummary.avgBill)}</strong></div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', background: colors.background, padding: '1rem', borderRadius: '8px' }}>
          <h3 style={headerStyle}>Payment Mix</h3>
          {exportButtons(
            'bar_payment_mix',
            [
              { label: 'Payment Type', value: (r) => r.payment_type },
              { label: 'Bills', value: (r) => r.bills },
              { label: 'Amount', value: (r) => fmt(r.amount) }
            ],
            paymentMixRows
          )}
        </div>
        <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead><tr><th style={thStyle}>Payment Type</th><th style={thStyle}>Bills</th><th style={thStyle}>Amount</th></tr></thead>
          <tbody>
            {paymentMixRows.length === 0 ? (
              <tr><td style={tdStyle} colSpan={3}>No data</td></tr>
            ) : paymentMixRows.map((r) => (
              <tr key={r.payment_type}><td style={tdStyle}>{r.payment_type}</td><td style={tdStyle}>{r.bills}</td><td style={tdStyle}>{fmt(r.amount)}</td></tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem', gap: '0.5rem', flexWrap: 'wrap', background: colors.background, padding: '1rem', borderRadius: '8px' }}>
          <h3 style={headerStyle}>Demand Forecast &amp; Stock-Out Risk</h3>
          {exportButtons(
            'bar_demand_forecast_stockout_risk',
            [
              { label: 'Item', value: (r) => r.item_name || '' },
              { label: 'Category', value: (r) => r.category_name || '' },
              { label: 'Trend', value: (r) => r.trend || '' },
              { label: 'Confidence', value: (r) => r.confidence || '' },
              { label: 'Predicted Qty (7d)', value: (r) => r.forecast_total_qty || 0 },
              { label: 'Predicted ml/day', value: (r) => r.predicted_ml_per_day || 0 },
              { label: 'Current Stock (ml)', value: (r) => r.current_stock_ml ?? '' },
              { label: 'Reorder Level (ml)', value: (r) => r.reorder_level_ml ?? '' },
              { label: 'Days Until Stockout', value: (r) => r.days_until_stockout ?? '' },
              { label: 'Risk', value: (r) => r.stock_risk || '' }
            ],
            forecastItems
          )}
        </div>
        <div style={{ fontSize: '0.8rem', color: colors.textLight, marginBottom: '0.75rem', padding: '0 0.25rem' }}>
          Day-of-week baseline + recent-trend adjustment per liquor item, projected against current stock. Deterministic (no AI). Only single-liquor items with a resolvable bar_inventory link get a stock-out projection — cocktails and multi-ingredient drinks show demand only.
        </div>
        <div style={tableContainerStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Item</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Trend</th>
                <th style={thStyle}>Confidence</th>
                <th style={thStyle}>Predicted Qty (7d)</th>
                <th style={thStyle}>Current Stock (ml)</th>
                <th style={thStyle}>Days Until Stockout</th>
                <th style={thStyle}>Risk</th>
              </tr>
            </thead>
            <tbody>
              {forecastItems.length === 0 ? (
                <tr><td style={tdStyle} colSpan={8}>No forecast data yet — needs recent sales history.</td></tr>
              ) : forecastItems.map((r) => {
                const riskColors = {
                  CRITICAL: '#c0392b', HIGH: '#e67e22', MEDIUM: '#f1c40f', BELOW_REORDER: '#f1c40f', OK: colors.success
                };
                const riskColor = r.stock_risk ? (riskColors[r.stock_risk] || colors.textLight) : colors.textLight;
                const trendArrow = r.trend === 'UP' ? '▲' : r.trend === 'DOWN' ? '▼' : '–';
                return (
                  <tr key={r.menu_item_id}>
                    <td style={tdStyle}>{r.item_name}</td>
                    <td style={tdStyle}>{r.category_name}</td>
                    <td style={tdStyle}>{trendArrow} {r.trend}</td>
                    <td style={tdStyle}>{r.confidence}</td>
                    <td style={tdStyle}>{r.forecast_total_qty}</td>
                    <td style={tdStyle}>{r.current_stock_ml ?? '—'}</td>
                    <td style={tdStyle}>{r.days_until_stockout ?? '—'}</td>
                    <td style={tdStyle}>
                      {r.stock_risk
                        ? <span style={{ padding: '0.2rem 0.6rem', borderRadius: '999px', background: riskColor, color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>{r.stock_risk.replace('_', ' ')}</span>
                        : <span style={{ color: colors.textLight }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.5rem', flexWrap: 'wrap', background: colors.background, padding: '1rem', borderRadius: '8px' }}>
          <h3 style={headerStyle}>Low Stock (Reorder)</h3>
          {exportButtons(
            'bar_low_stock_reorder',
            [
              { label: 'Item Code', value: (r) => r.item_code },
              { label: 'Available (Base)', value: (r) => fmt(r.available_quantity_ml) },
              { label: 'Reorder Level', value: (r) => fmt(r.reorder_level) },
              { label: 'Shortage', value: (r) => fmt(r.shortage) }
            ],
            lowStockRows
          )}
        </div>
        <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead><tr><th style={thStyle}>Item Code</th><th style={thStyle}>Available (Base)</th><th style={thStyle}>Reorder Level</th><th style={thStyle}>Shortage</th></tr></thead>
          <tbody>
            {lowStockRows.length === 0 ? (
              <tr><td style={tdStyle} colSpan={4}>No low stock items</td></tr>
            ) : lowStockRows.map((r) => (
              <tr key={r.item_code}><td style={tdStyle}>{r.item_code}</td><td style={tdStyle}>{fmt(r.available_quantity_ml)}</td><td style={tdStyle}>{fmt(r.reorder_level)}</td><td style={tdStyle}>{fmt(r.shortage)}</td></tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.5rem', flexWrap: 'wrap', background: colors.background, padding: '1rem', borderRadius: '8px' }}>
          <h3 style={headerStyle}>Stock Adjustment Register</h3>
          {exportButtons(
            'bar_stock_adjustment_register',
            [
              { label: 'Date/Time', value: (r) => (r.created_at ? fmtDateTime(r.created_at) : '-') },
              { label: 'Item', value: (r) => r.item_code },
              { label: 'Source Menu Item', value: (r) => (r.source_menu_item_name ? `Adjusted via Menu Item: ${r.source_menu_item_name}` : '-') },
              { label: 'Mode', value: (r) => r.adjustment_mode },
              { label: 'Direction', value: (r) => r.direction },
              { label: 'Input Qty', value: (r) => fmt(r.input_qty) },
              { label: 'Delta Bottles', value: (r) => fmt(r.delta_bottles) },
              { label: 'Delta Base', value: (r) => fmt(r.delta_ml) },
              { label: 'User', value: (r) => r.adjusted_by || '-' },
              { label: 'Reason', value: (r) => r.reason || '-' }
            ],
            filteredAdjustments
          )}
        </div>
        <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Date/Time</th>
              <th style={thStyle}>Item</th>
              <th style={thStyle}>Source</th>
              <th style={thStyle}>Mode</th>
              <th style={thStyle}>Direction</th>
              <th style={thStyle}>Input Qty</th>
              <th style={thStyle}>Delta Bottles</th>
              <th style={thStyle}>Delta Base</th>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdjustments.length === 0 ? (
              <tr><td style={tdStyle} colSpan={10}>No adjustments</td></tr>
            ) : filteredAdjustments.map((a) => (
              <tr key={a.id}>
                <td style={tdStyle}>{a.created_at ? fmtDateTime(a.created_at) : '-'}</td>
                <td style={tdStyle}>{a.item_code}</td>
                <td style={tdStyle}>{a.source_menu_item_name ? `Adjusted via Menu Item: ${a.source_menu_item_name}` : '-'}</td>
                <td style={tdStyle}>{a.adjustment_mode}</td>
                <td style={tdStyle}>{a.direction}</td>
                <td style={tdStyle}>{fmt(a.input_qty)}</td>
                <td style={tdStyle}>{fmt(a.delta_bottles)}</td>
                <td style={tdStyle}>{fmt(a.delta_ml)}</td>
                <td style={tdStyle}>{a.adjusted_by || '-'}</td>
                <td style={tdStyle}>{a.reason || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

export default BarReports;
